import React from 'react';

import {
	ApButtonSecondary,
	ApButtonMain,
	ApButtonGroup,
	ApRadio,
	ApLoaderDefault,
	ApLoaderText,
} from '@alixpartners/ui-components';
import { PeriodConfigStep } from './PeriodConfigStep';
import { DimensionStep } from './DimensionStep';
import { getExcelDimensions, getExcelTaxonomy } from 'utils/apiRequests';
import {
	StyledModal,
	RadioGroup,
	RadioWrapper,
	Title,
	LoaderWrapper,
} from './DownloadDataModal.styles';
import isEqual from 'lodash/isEqual';

export const TYPES = {
	SPEND_CUBE: 1,
	CATEGORY: 2,
	INITIATIVE: 3,
};

export const STEPS = {
	INITIAL: 1,
	TAXONOMY: 2,
	DIMENSION: 3,
	TIME_PERIOD: 4,
};

export const TAXONOMY_TYPES = {
	AP: 3,
	CLIENT: 2,
	PROJECT: 1,
};

export class DownloadDataModal extends React.Component {
	state = {
		activeStep: STEPS.INITIAL,
		type: TYPES.SPEND_CUBE,
		params: null,
		dimensionsData: null,
		taxonomyData: null,
	};

	componentDidMount() {
		this.fetchDimensions();
		this.fetchTaxonomy();
	}

	componentDidUpdate(prevProps, prevState) {
		const { type, taxonomyData } = this.state;

		if (
			prevState.type !== type ||
			!isEqual(prevState.taxonomyData, taxonomyData)
		) {
			this.setDefaultParams();
		}
	}

	fetchTaxonomy = () => {
		return getExcelTaxonomy().then(taxonomyData => {
			this.setState({ taxonomyData });
		});
	};

	setDefaultParams = () => {
		const { taxonomyData, type } = this.state;

		if (type === TYPES.INITIATIVE) {
			this.setState({
				params: {
					indirectTaxonomyType: TAXONOMY_TYPES.PROJECT,
					directTaxonomyType: TAXONOMY_TYPES.PROJECT,
				},
			});

			return;
		}

		if (type === TYPES.SPEND_CUBE) {
			this.setState({
				params: null,
			});

			return;
		}

		if (!taxonomyData) {
			return;
		}

		const defaultTaxonomies = {
			...(taxonomyData.direct
				? {
						directTaxonomyType: taxonomyData.direct.project
							? TAXONOMY_TYPES.PROJECT
							: TAXONOMY_TYPES.CLIENT,
				  }
				: { directTaxonomyType: null }),
			...(taxonomyData.indirect
				? {
						indirectTaxonomyType: taxonomyData.indirect.project
							? TAXONOMY_TYPES.PROJECT
							: TAXONOMY_TYPES.CLIENT,
				  }
				: { indirectTaxonomyType: null }),
		};

		this.setState({ params: { ...defaultTaxonomies } });
	};

	fetchDimensions = () => {
		return getExcelDimensions().then(dimensionsData => {
			this.setState({ dimensionsData });
		});
	};

	changeStep = activeStep => {
		this.setState({ activeStep });
	};

	changeType = e => {
		const type = Number(e.target.value);

		this.setState({
			...(type === TYPES.SPEND_CUBE ? { params: null } : {}),
			type,
		});
	};

	changeTaxonomy = e => {
		const { params } = this.state;
		const type = Number(e.target.value);
		const taxonomyName = e.target.name;

		this.setState({
			params: {
				...(params || {}),
				[taxonomyName]: type,
			},
		});
	};

	changeParams = (updates, shouldAppend) => {
		const { params } = this.state;

		this.setState({
			params: {
				...(params && shouldAppend ? { ...params } : {}),
				...updates,
			},
		});
	};

	generateAndDownload = () => {
		const { onSubmit, onClose } = this.props;
		const { params, type } = this.state;

		if (!params) {
			return;
		}

		const requestParams = { ...params };

		if (type !== TYPES.SPEND_CUBE) {
			requestParams.exportType = type;
		}

		onSubmit(requestParams);
		onClose();
	};

	renderFooter = () => {
		const { activeStep, params, type } = this.state;

		return (
			<ApButtonGroup justifyContent="space-between">
				{activeStep === 1 ? (
					<div />
				) : (
					<ApButtonSecondary
						onClick={() => {
							const nextStep =
								activeStep === 4 ||
								(type === TYPES.INITIATIVE && activeStep === 3)
									? 1
									: activeStep - 1;

							this.changeStep(nextStep);
						}}
					>
						Back
					</ApButtonSecondary>
				)}

				{activeStep === 3 || activeStep === 4 ? (
					<ApButtonMain onClick={this.generateAndDownload} disabled={!params}>
						Generate & download
					</ApButtonMain>
				) : (
					<ApButtonSecondary
						onClick={() => {
							const nextStep =
								activeStep === 1 && type === TYPES.SPEND_CUBE
									? 4
									: activeStep === 1 && type === TYPES.INITIATIVE
										? 3
										: activeStep + 1;

							this.changeStep(nextStep);
						}}
					>
						Next
					</ApButtonSecondary>
				)}
			</ApButtonGroup>
		);
	};

	render() {
		const { onClose } = this.props;
		const {
			activeStep,
			type,
			dimensionsData,
			taxonomyData,
			params,
		} = this.state;

		return (
			<StyledModal
				header="Spend Cube download configuration"
				isDismissible
				footer={() => this.renderFooter()}
				hasClosed={onClose}
				isOpen
			>
				{activeStep === 1 && (
					<React.Fragment>
						Please choose one of the download options from the list below
						<RadioGroup>
							<RadioWrapper>
								<ApRadio
									name="type"
									value={TYPES.SPEND_CUBE}
									checked={type === TYPES.SPEND_CUBE}
									onChange={this.changeType}
								>
									Spend Cube
								</ApRadio>
							</RadioWrapper>
							<RadioWrapper>
								<ApRadio
									name="type"
									value={TYPES.CATEGORY}
									checked={type === TYPES.CATEGORY}
									onChange={this.changeType}
								>
									Total spend by category
								</ApRadio>
							</RadioWrapper>
							<RadioWrapper>
								<ApRadio
									name="type"
									value={TYPES.INITIATIVE}
									checked={type === TYPES.INITIATIVE}
									onChange={this.changeType}
								>
									Total spend by initiative
								</ApRadio>
							</RadioWrapper>
						</RadioGroup>
					</React.Fragment>
				)}
				{activeStep === 2 &&
					(taxonomyData ? (
						<React.Fragment>
							{taxonomyData.direct && (
								<React.Fragment>
									<Title>Direct spend taxonomy</Title>

									<RadioWrapper disabled={!taxonomyData.direct.project}>
										<ApRadio
											name="directTaxonomyType"
											value={TAXONOMY_TYPES.PROJECT}
											checked={
												params &&
												params.directTaxonomyType === TAXONOMY_TYPES.PROJECT
											}
											disabled={!taxonomyData.direct.project}
											onChange={this.changeTaxonomy}
										>
											Project taxonomy
										</ApRadio>
									</RadioWrapper>
									<RadioWrapper>
										<ApRadio
											name="directTaxonomyType"
											value={TAXONOMY_TYPES.CLIENT}
											checked={
												params &&
												params.directTaxonomyType === TAXONOMY_TYPES.CLIENT
											}
											onChange={this.changeTaxonomy}
										>
											Client taxonomy
										</ApRadio>
									</RadioWrapper>
								</React.Fragment>
							)}
							{taxonomyData.indirect && (
								<React.Fragment>
									<Title>Indirect spend taxonomy</Title>
									<RadioWrapper disabled={!taxonomyData.indirect.project}>
										<ApRadio
											name="indirectTaxonomyType"
											value={TAXONOMY_TYPES.PROJECT}
											checked={
												params &&
												params.indirectTaxonomyType === TAXONOMY_TYPES.PROJECT
											}
											onChange={this.changeTaxonomy}
											disabled={!taxonomyData.indirect.project}
										>
											Project taxonomy
										</ApRadio>
									</RadioWrapper>
									<RadioWrapper>
										<ApRadio
											name="indirectTaxonomyType"
											value={TAXONOMY_TYPES.AP}
											checked={
												params &&
												params.indirectTaxonomyType === TAXONOMY_TYPES.AP
											}
											onChange={this.changeTaxonomy}
										>
											AP taxonomy
										</ApRadio>
									</RadioWrapper>
									<RadioWrapper>
										<ApRadio
											name="indirectTaxonomyType"
											value={TAXONOMY_TYPES.CLIENT}
											checked={
												params &&
												params.indirectTaxonomyType === TAXONOMY_TYPES.CLIENT
											}
											onChange={this.changeTaxonomy}
										>
											Client taxonomy
										</ApRadio>
									</RadioWrapper>
								</React.Fragment>
							)}
						</React.Fragment>
					) : (
						<LoaderWrapper>
							<ApLoaderDefault loaderText={() => <ApLoaderText />} />
						</LoaderWrapper>
					))}
				{activeStep === 3 && (
					<DimensionStep
						data={dimensionsData}
						onDimensionChange={this.changeParams}
						params={params}
					/>
				)}
				{activeStep === 4 && (
					<PeriodConfigStep onParamsChange={this.changeParams} />
				)}
			</StyledModal>
		);
	}
}
